/* eslint-disable react/react-in-jsx-scope */
import {
  ConfirmationModal,
  CustomButton,
  CustomDrawer,
  CustomSwitch,
  DateInput,
} from '@components';
import {
  Advisor,
  ClientOrganizationInput as BaseClientOrganizationInput,
  BillingInterval,
  BillingModel,
  ClientLanguage,
  ClientOrganization,
  Entitlement,
  OrganizationStatus,
  SuperAdmin,
  SuperAdminRole,
  Tier,
  TierCodes,
  OrganizationTierCategory,
} from '@graphql';
import AddIcon from '@mui/icons-material/Add';
import BorderColorOutlined from '@mui/icons-material/BorderColorOutlined';
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { appContext, capitalizeFirstLetter, nameFieldSchema, NameFieldProfile, sortByProperty } from '@utils';
import { FormikProps, useFormik } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { COLORS } from '@styles';
import { ReactComponent as DashedDivider } from '@assets/svg/DashedDivider.svg';
import * as Yup from 'yup';
import { ArrowBackIos, SyncAlt, InfoOutlined, ErrorOutline } from '@mui/icons-material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import {
  organizationStatusOptions,
  CreateOrganizationSteps,
  PlatformFeatures,
  isOrgTCRFeatureFlagActive,
  CreateOrganizationStepConfig,
  getBillingCategory,
} from '../../utils';

const sx = {
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
  } as React.CSSProperties,
  editHeader: {
    paddingBottom: '1rem',
    borderBottom: `1px dashed ${COLORS.NEUTRAL_LIGHT_3}`,
  },
  dashedDivider: {
    width: '100%',
  },
  formContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    padding: '0 1rem',
  } as React.CSSProperties,
  headerOrgId: {
    color: COLORS.NEUTRAL_LIGHT_0,
  },
  formItems: {
    margin: '1rem 0',
    borderRadius: '1rem',
  },
  inputBorder: {
    borderRadius: '1rem',
  },
  disabledMenuItem: {
    pointerEvents: 'auto !important',
    cursor: 'not-allowed !important',
  },
  checkbox: {
    '&.Mui-checked': {
      color: COLORS.ELEKTRAFI_NEUTRAL,
    },
  },
  checkboxLabel: {
    margin: '0.5rem 0 0 1rem',
  },
  checkboxLabelIndividual: {
    margin: '0.5rem 0 0 0',
  },
  section: {
    margin: '1.5rem 0 0.5rem 0',
  },
  button: {
    padding: '1.5rem 1rem 0 1rem',
    borderTop: `1px solid ${COLORS.NEUTRAL_DARK_2}`,
    marginTop: 'auto',
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  fullsizeSpan: { width: '100%' },
  formFlexContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'space-between',
  } as React.CSSProperties,
  switchContainer: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: '1rem',
  } as React.CSSProperties,
  switchLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: COLORS.NEUTRAL_LIGHT_3,
  } as React.CSSProperties,
};

type ClientOrganizationInput = BaseClientOrganizationInput & {
  id: string;
  purchasedSeats: number | string;
  [PlatformFeatures.HRIS]: boolean;
  [PlatformFeatures.EQUITY]: boolean;
  [PlatformFeatures.TCR]: boolean;
  currentTierId: string;
  isPayingCustomer: boolean;
  addons: Record<string, boolean>;
  billingInterval: BillingInterval;
  billingModel: BillingModel;
  startsAt: string;
  endsAt: string;
  trialPeriodDays?: number;
};

interface OrganizationDetailsStepProps {
  isEdit: boolean;
  org?: ClientOrganization;
  formik: FormikProps<ClientOrganizationInput>;
  advisors: Advisor[];
  managers: SuperAdmin[];
  currentUser: SuperAdmin;
  isAccountManager: boolean;
  previewModeEnabled: boolean;
  previewModePending: boolean;
  onTogglePreviewMode: () => void;
  demoEmployeesEnabled: boolean;
  demoEmployeesPending: boolean;
  onToggleDemoEmployees: () => void;
  /** `null` when the current setting could not be loaded. */
  analyticsEventsEnabled: boolean | null;
  analyticsEventsPending: boolean;
  onToggleAnalyticsEvents: () => void;
  onRetryAnalyticsEvents: () => void;
}
const ClientLanguageLabels: Record<ClientLanguage, string> = {
  [ClientLanguage.English]: 'English',
  [ClientLanguage.French]: 'French',
};

function OrganizationDetailsStep(props: OrganizationDetailsStepProps) {
  const {
    isEdit,
    org,
    formik,
    advisors,
    managers,
    currentUser,
    isAccountManager,
    previewModeEnabled,
    previewModePending,
    onTogglePreviewMode,
    demoEmployeesEnabled,
    demoEmployeesPending,
    onToggleDemoEmployees,
    analyticsEventsEnabled,
    analyticsEventsPending,
    onToggleAnalyticsEvents,
    onRetryAnalyticsEvents,
  } = props;

  // Sort the advisors by whether they have a booking URL
  const sortedAdvisors = useMemo(() => {
    if (!advisors) return [];
    return sortByProperty(advisors, 'bookingUrl') as Advisor[];
  }, [advisors]);

  const customHandleFormikChange = (e: SelectChangeEvent<string | null>) => {
    if (e.target.name === 'advisorId') {
      if (advisors?.find((advisor) => advisor.id === e.target.value)?.bookingUrl) {
        formik.setFieldValue('advisorId', e.target.value);
      }
    }
  };

  const accountManagerOptions = useMemo(() => {
    if (!isAccountManager) return [];

    const options = [currentUser];
    if (org?.admin && org.admin.id !== currentUser.id) {
      options.push(org.admin as SuperAdmin);
    }

    return options;
  }, [isAccountManager, currentUser, org]);

  return (
    <>
      {isEdit ? (
        <>
          <Typography variant="h6">{org?.name}</Typography>
          <Typography sx={sx.headerOrgId} variant="subtitle1">
            {org?.id}
          </Typography>
          <DashedDivider style={sx.dashedDivider} />
        </>
      ) : (
        <Typography variant="subtitle1">Please provide the organization information</Typography>
      )}
      <Typography sx={sx.section} variant="subtitle2">
        Organization Information
      </Typography>
      <TextField
        style={sx.formItems}
        fullWidth
        label="Organization Name"
        name="name"
        onChange={formik.handleChange}
        value={formik.values.name}
        variant="outlined"
        InputProps={{ style: sx.inputBorder }}
        error={!!formik.errors.name}
        helperText={formik.errors.name}
      />
      {isEdit && (
        <FormControl fullWidth>
          <InputLabel sx={sx.formItems}>Organization Status</InputLabel>
          <Select
            sx={sx.formItems}
            fullWidth
            name="status"
            required
            onChange={formik.handleChange}
            value={formik.values.status}
            label="Organization Status"
          >
            {organizationStatusOptions.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <div style={sx.switchContainer}>
        <Typography sx={sx.switchLabel} variant="subtitle1">
          Demo Account
        </Typography>
        <CustomSwitch
          checked={!formik.values.isPayingCustomer}
          onChange={(e) => formik.setFieldValue('isPayingCustomer', !e.target.checked)}
        />
      </div>
      {isEdit && (
        <div style={{ ...sx.switchContainer, marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <Typography sx={sx.switchLabel} variant="subtitle1">
              Preview Mode
            </Typography>
            <Tooltip title="Lets Client Admins in this organization preview the onboarding experience with sample data, without affecting real data.">
              <InfoOutlined sx={{ fontSize: '1rem', color: COLORS.NEUTRAL_LIGHT_3 }} />
            </Tooltip>
          </div>
          {previewModePending ? (
            <CircularProgress size={20} sx={{ color: COLORS.ELEKTRAFI_NEUTRAL }} />
          ) : (
            <CustomSwitch checked={previewModeEnabled} onChange={onTogglePreviewMode} />
          )}
        </div>
      )}
      {isEdit && (
        <div style={{ ...sx.switchContainer, marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <Typography sx={sx.switchLabel} variant="subtitle1">
              Test employees
            </Typography>
            <Tooltip title="Lets Client Admins in this organization add test employees. They show up in the employee list like real employees, but can never log in, receive emails or invitations, or appear in analytics.">
              <InfoOutlined sx={{ fontSize: '1rem', color: COLORS.NEUTRAL_LIGHT_3 }} />
            </Tooltip>
          </div>
          {demoEmployeesPending ? (
            <CircularProgress size={20} sx={{ color: COLORS.ELEKTRAFI_NEUTRAL }} />
          ) : (
            <CustomSwitch checked={demoEmployeesEnabled} onChange={onToggleDemoEmployees} />
          )}
        </div>
      )}
      {isEdit && (
        <div style={{ ...sx.switchContainer, marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <Typography sx={sx.switchLabel} variant="subtitle1">
              Analytics events
            </Typography>
            <Tooltip title="Sends Amplitude and Intercom events for users in this organization. Defaults to on in prod and off in stage.">
              <InfoOutlined sx={{ fontSize: '1rem', color: COLORS.NEUTRAL_LIGHT_3 }} />
            </Tooltip>
          </div>
          {analyticsEventsPending && (
            <CircularProgress size={20} sx={{ color: COLORS.ELEKTRAFI_NEUTRAL }} />
          )}
          {!analyticsEventsPending && analyticsEventsEnabled === null && (
            <Tooltip title="Couldn't load the current setting. Click to retry.">
              <IconButton
                size="small"
                aria-label="Retry loading analytics events setting"
                onClick={onRetryAnalyticsEvents}
              >
                <ErrorOutline color="error" sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
          )}
          {!analyticsEventsPending && analyticsEventsEnabled !== null && (
            <CustomSwitch checked={analyticsEventsEnabled} onChange={onToggleAnalyticsEvents} />
          )}
        </div>
      )}
      <Typography sx={sx.section} variant="subtitle2">
        Organization Management
      </Typography>
      <FormControl fullWidth>
        <InputLabel error={!!formik.errors.managerId} shrink sx={sx.formItems}>
          Manager
        </InputLabel>
        <Select
          sx={sx.formItems}
          fullWidth
          name="managerId"
          displayEmpty
          disabled={isAccountManager}
          onChange={formik.handleChange}
          value={formik.values.managerId}
          input={<OutlinedInput notched label="Manager" />}
          error={!!formik.errors.managerId}
        >
          <MenuItem value="" disabled>
            Select an Account Manager
          </MenuItem>
          {(isAccountManager ? accountManagerOptions : managers).map((manager) => (
            <MenuItem key={manager.id} value={manager.id}>{`${manager.name}`}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel error={!!formik.errors.advisorId} shrink sx={sx.formItems}>
          Advisor
        </InputLabel>
        <Select
          sx={sx.formItems}
          fullWidth
          displayEmpty
          name="advisorId"
          onChange={(e) => customHandleFormikChange(e)}
          value={formik.values.advisorId}
          input={<OutlinedInput notched label="Advisor" />}
          error={!!formik.errors.advisorId}
        >
          <MenuItem value="" disabled>
            Select an Advisor
          </MenuItem>
          {sortedAdvisors?.map((advisor) => {
            // Determine if the advisor is disabled
            const isDisabled = !advisor.bookingUrl;

            // Tooltip title
            const tooltipTitle = isDisabled
              ? "This advisor cannot be assigned because they don't have a booking URL."
              : '';

            return (
              <MenuItem
                key={advisor.id}
                sx={isDisabled ? sx.disabledMenuItem : {}}
                value={advisor.id}
                disabled={isDisabled}
              >
                <Tooltip key={advisor.id} title={tooltipTitle}>
                  <span style={sx.fullsizeSpan}>{`${advisor.firstName} ${advisor.lastName}`}</span>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </>
  );
}

interface OrganizationBillingStepProps {
  formik: FormikProps<ClientOrganizationInput>;
  tiers: Tier[];
  tierAddons: Entitlement[];
}

const BillingModelLabels: Record<BillingModel, string> = {
  [BillingModel.CREDIT_CARD]: 'Credit Card',
  [BillingModel.INVOICE]: 'Invoice',
};

function OrganizationBillingStep(props: OrganizationBillingStepProps) {
  const { formik, tiers, tierAddons } = props;

  const isCreditCard = formik.values.billingModel === BillingModel.CREDIT_CARD;
  const isInvoice = formik.values.billingModel === BillingModel.INVOICE;

  const billingIntervalOptions = useMemo(
    () =>
      Object.values(BillingInterval).map((interval) => ({
        label: capitalizeFirstLetter(interval),
        value: interval,
      })),
    [],
  );

  const tierSelectOptions = useMemo(
    () =>
      tiers.map((tier) => {
        const isEnterpriseTier = tier?.code
          ? getBillingCategory(tier.code as TierCodes) === OrganizationTierCategory.Enterprise
          : false;

        return {
          label: tier.name,
          value: tier.id,
          disabled: isCreditCard && isEnterpriseTier,
        };
      }),
    [tiers, isCreditCard],
  );

  const handleBillingModelChange = (event: SelectChangeEvent<BillingModel>) => {
    const nextValue = event.target.value as BillingModel;
    const currentTier = tiers.find((tier) => tier.id === formik.values.currentTierId);
    const isCurrentTierEnterprise = currentTier?.code
      ? getBillingCategory(currentTier.code as TierCodes) === OrganizationTierCategory.Enterprise
      : false;

    if (nextValue === BillingModel.CREDIT_CARD && isCurrentTierEnterprise) {
      toast.error(
        'Credit card billing isn’t supported for the Enterprise tier. Please choose Core or Pro before switching.',
      );
      return;
    }

    // Reset trial period days if billing model is changed to invoice
    if (nextValue === BillingModel.INVOICE) {
      formik.setFieldValue('trialPeriodDays', '');
    }

    if (nextValue === BillingModel.INVOICE) {
      formik.setFieldValue('billingInterval', BillingInterval.yearly);
    }

    formik.handleChange(event);
  };

  // TODO: Commented out for now because we don't support creating a customer for credit card billing model
  // const billingModelOptions = useMemo(
  //   () =>
  //     Object.values(BillingModel).map((model) => ({
  //       label: BillingModelLabels[model],
  //       value: model,
  //     })),
  //   [],
  // );

  return (
    <>
      <Typography variant="subtitle1">Please provide the organization information</Typography>
      <div style={sx.formFlexContainer}>
        <FormControl fullWidth>
          <InputLabel sx={sx.formItems}>Organization Plan</InputLabel>
          <Select
            sx={sx.formItems}
            fullWidth
            name="currentTierId"
            required
            onChange={formik.handleChange}
            value={formik.values.currentTierId}
            label="Organization Plan"
          >
            {tierSelectOptions.map(({ label, value, disabled }) => (
              <MenuItem key={value} value={value} disabled={disabled}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          style={sx.formItems}
          fullWidth
          label="N° of Seats"
          name="purchasedSeats"
          onChange={formik.handleChange}
          value={formik.values.purchasedSeats}
          variant="outlined"
          type="number"
          InputProps={{ style: sx.inputBorder }}
          error={!!formik.errors.purchasedSeats}
          helperText={formik.errors.purchasedSeats}
          placeholder="Seats"
        />
      </div>

      {tierAddons.length > 0 && (
        <Typography sx={sx.section} variant="subtitle2">
          Platform Add-Ons
        </Typography>
      )}
      {tierAddons.map((addon) => (
        <FormControlLabel
          key={addon.feature.featureFlagKey}
          sx={sx.checkboxLabel}
          control={
            <Checkbox
              name={`${addon.feature.featureFlagKey}`}
              checked={formik?.values?.addons?.[addon?.feature?.featureFlagKey] ?? false}
              onChange={(_, checked) =>
                formik.setFieldValue('addons', {
                  ...formik.values.addons,
                  [addon?.feature?.featureFlagKey]: checked,
                })
              }
              sx={sx.checkbox}
            />
          }
          label={addon?.feature?.name}
        />
      ))}
      <Typography sx={sx.section} variant="subtitle2">
        Organization Management
      </Typography>
      <FormControl fullWidth>
        <InputLabel sx={sx.formItems}>Payment Method</InputLabel>
        <Select
          sx={sx.formItems}
          fullWidth
          name="billingModel"
          required
          onChange={handleBillingModelChange}
          value={formik.values.billingModel}
          label="Payment Method"
          disabled={!formik.values.isPayingCustomer}
        >
          {Object.values(BillingModel).map((billingModel) => (
            <MenuItem key={billingModel} value={billingModel}>
              {BillingModelLabels[billingModel]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel sx={sx.formItems}>Payment Frequency</InputLabel>
        <Select
          sx={sx.formItems}
          fullWidth
          name="billingInterval"
          required
          onChange={formik.handleChange}
          value={formik.values.billingInterval}
          label="Payment Frequency"
          disabled={!formik.values.isPayingCustomer || isInvoice}
        >
          {billingIntervalOptions.map((interval) => (
            <MenuItem key={interval.value} value={interval.value}>
              {interval.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <DateInput
          label="Starting Date"
          name="startsAt"
          value={formik.values.startsAt}
          setFieldValue={formik.setFieldValue}
          error={!!formik.errors.startsAt}
          disabled={!formik.values.isPayingCustomer || isCreditCard}
          onChange={(value) => {
            formik.setFieldValue('startsAt', value);
            if (dayjs(value).isValid()) {
              const endsAt = dayjs(value).add(1, 'year').add(3, 'hours');
              formik.setFieldValue('endsAt', endsAt);
            }
          }}
        />
        {dayjs(formik.values.endsAt).isValid() && (
          <Typography
            sx={{ marginTop: '0.25rem', marginLeft: '1rem', color: COLORS.NEUTRAL_LIGHT_0 }}
            variant="body2"
          >
            Ending Date: {dayjs(formik.values.endsAt).format('YYYY-MM-DD')}
          </Typography>
        )}
      </FormControl>
      <FormControl fullWidth>
        <TextField
          style={sx.formItems}
          fullWidth
          label="Number of Trial Period Days (Optional)"
          name="trialPeriodDays"
          onChange={formik.handleChange}
          value={formik.values.trialPeriodDays}
          variant="outlined"
          type="number"
          InputProps={{
            style: sx.inputBorder,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="If not provided, defaults to 14 days">
                  <IconButton edge="end" size="small" sx={{ color: COLORS.NEUTRAL_LIGHT_3 }}>
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!formik.errors.trialPeriodDays}
          helperText={formik.errors.trialPeriodDays}
          placeholder="Trial Period Days"
          disabled={formik.values.billingModel === BillingModel.INVOICE}
        />
      </FormControl>
    </>
  );
}

interface OrganizationAdminStepProps {
  formik: FormikProps<ClientOrganizationInput>;
}

function OrganizationAdminStep(props: OrganizationAdminStepProps) {
  const { formik } = props;

  const languageSelectOptions = useMemo(
    () =>
      Object.values(ClientLanguage).map((lang) => ({
        label: ClientLanguageLabels[lang],
        value: lang,
      })),
    [],
  );

  return (
    <>
      <Typography variant="subtitle1">Please provide the organization admin information</Typography>
      <Typography sx={sx.section} variant="subtitle2">
        Organization Administrator
      </Typography>
      <TextField
        style={sx.formItems}
        fullWidth
        label="First Name"
        name="firstName"
        onChange={formik.handleChange}
        value={formik.values.firstName}
        variant="outlined"
        InputProps={{ style: sx.inputBorder }}
        error={!!formik.errors.firstName}
        helperText={formik.errors.firstName}
      />
      <TextField
        style={sx.formItems}
        fullWidth
        label="Last Name"
        name="lastName"
        onChange={formik.handleChange}
        value={formik.values.lastName}
        variant="outlined"
        InputProps={{ style: sx.inputBorder }}
        error={!!formik.errors.lastName}
        helperText={formik.errors.lastName}
      />
      <TextField
        style={sx.formItems}
        fullWidth
        label="Work Email"
        name="email"
        onChange={formik.handleChange}
        value={formik.values.email}
        variant="outlined"
        InputProps={{ style: sx.inputBorder }}
        error={!!formik.errors.email}
        helperText={formik.errors.email}
      />
      <FormControl fullWidth>
        <InputLabel sx={sx.formItems}>Preferred Language</InputLabel>
        <Select
          sx={sx.formItems}
          fullWidth
          name="preferredLanguage"
          onChange={formik.handleChange}
          value={formik.values.preferredLanguage}
          label="Preferred Language"
        >
          {languageSelectOptions.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
}

interface IOrganizationDrawerProps {
  open: boolean;
  onClose: () => void;
  isEdit?: boolean;
  org?: ClientOrganization;
  advisors?: Advisor[];
}

export const OrganizationDrawer = observer(
  ({ open, onClose, isEdit = false, org, advisors }: IOrganizationDrawerProps) => {
    const [createOrganizationStep, setCreateOrganizationStep] = React.useState(
      CreateOrganizationSteps.ORGANIZATION_DETAILS,
    );

    const [isPayCustomerChangedModalOpen, setIsPayCustomerChangedModalOpen] = React.useState(false);

    const [previewModeEnabled, setPreviewModeEnabled] = React.useState(false);
    const [previewModePending, setPreviewModePending] = React.useState(false);

    const [demoEmployeesEnabled, setDemoEmployeesEnabled] = React.useState(false);
    const [demoEmployeesPending, setDemoEmployeesPending] = React.useState(false);

    const [analyticsEventsEnabled, setAnalyticsEventsEnabled] = React.useState<boolean | null>(
      null,
    );
    const [analyticsEventsPending, setAnalyticsEventsPending] = React.useState(false);
    const [analyticsEventsReloadKey, setAnalyticsEventsReloadKey] = React.useState(0);

    const {
      organizationStore: {
        createClientOrg,
        updateOrganization,
        refreshOrganizationViews,
        orgIdsByFeatureFlagConfig,
        getOrgIdsByFeatureFlagConfig,
        changeOrganizationPreviewMode,
        getOrganizationPreviewModeStatus,
        changeOrganizationDemoEmployees,
        getOrganizationDemoEmployeesStatus,
        changeOrganizationAnalyticsEvents,
        getOrganizationAnalyticsEventsStatus,
      },
      managersStore: { managers },
      userStore: { user },
      tiersStore: { tiers, getTierAddons },
      billingStore: { createStripeCustomer },
    } = useContext(appContext);

    const isTCRActive = useMemo(
      () => isOrgTCRFeatureFlagActive(org?.id || '', orgIdsByFeatureFlagConfig),
      [orgIdsByFeatureFlagConfig, org],
    );

    const isAccountManager = user?.adminRole === SuperAdminRole.Manager;

    const initialValues = useMemo<ClientOrganizationInput>(
      () => {
        const firstTierId = tiers && tiers.length > 0 ? tiers[0]?.id : '';
          return {
            id: org?.id || '',
            name: org?.name || '',
            status: org?.status || OrganizationStatus.Active,
            managerId: org?.admin?.id || (isAccountManager ? user.id : ''),
            advisorId: org?.advisor.id || '',
            currentTierId: org?.currentTier?.id || firstTierId || '',
            [PlatformFeatures.TCR]: isEdit ? isTCRActive : true,
            [PlatformFeatures.HRIS]: false,
            [PlatformFeatures.EQUITY]: false,
            purchasedSeats: '',
            billingInterval: BillingInterval.yearly,
            firstName: '',
            lastName: '',
            email: '',
            isPayingCustomer: org?.isPayingCustomer ?? true,
            preferredLanguage: ClientLanguage.English,
            billingModel: BillingModel.INVOICE,
            startsAt: '',
            endsAt: '',
            trialPeriodDays: undefined,
        } as ClientOrganizationInput;
      },
      [org, isTCRActive, isEdit, tiers, isAccountManager, user],
    );

    const handleEditOrganization = async (
      formValues: ClientOrganizationInput,
      checkIfPlanChanged = true,
    ) => {
      if (checkIfPlanChanged && formValues.isPayingCustomer !== org?.isPayingCustomer) {
        setIsPayCustomerChangedModalOpen(true);
        return null;
      }

      const payload = {
        id: formValues.id,
        name: formValues.name,
        status: formValues.status ?? OrganizationStatus.Active,
        managerId: formValues.managerId,
        advisorId: formValues.advisorId,
        isTcrActive: formValues[PlatformFeatures.TCR],
        isPayingCustomer: formValues.isPayingCustomer,
      };

      return updateOrganization(payload);
    };

    const validationSchema = Yup.object().shape({
      name: nameFieldSchema('Organization name', NameFieldProfile.Label).required('Organization Name is required.'),
      email: Yup.string()
        .email('Please provide a valid email address.')
        .when([], (_, schema) =>
          createOrganizationStep !== CreateOrganizationSteps.ADMIN_DETAILS
            ? schema
            : schema.required('Email is required.'),
        ),
      firstName: nameFieldSchema('First name', NameFieldProfile.Person).when([], (_, schema) =>
        createOrganizationStep !== CreateOrganizationSteps.ADMIN_DETAILS
          ? schema
          : schema.required('First name is required.'),
      ),
      lastName: nameFieldSchema('Last name', NameFieldProfile.Person).when([], (_, schema) =>
        createOrganizationStep !== CreateOrganizationSteps.ADMIN_DETAILS
          ? schema
          : schema.required('Last name is required.'),
      ),
      managerId: Yup.string().required('Manager is required.'),
      advisorId: Yup.string().required('Advisor is required.'),
      currentTierId: Yup.string().when([], (_, schema) => {
        if (createOrganizationStep === CreateOrganizationSteps.BILLING_DETAILS) {
          return schema.required('Organization Plan is required.');
        }

        return schema;
      }),
      purchasedSeats: Yup.number().when([], (_, schema) => {
        if (createOrganizationStep === CreateOrganizationSteps.BILLING_DETAILS) {
          return schema
            .required('N° of Seats is required.')
            .integer('N° of Seats must be an integer.')
            .min(1, 'N° of Seats must be at least 1.')
            .transform((value) => Number(value));
        }

        return schema;
      }),
      trialPeriodDays: Yup.number()
        .nullable()
        .when([], (_, schema) => {
          if (createOrganizationStep === CreateOrganizationSteps.BILLING_DETAILS) {
            return schema
              .transform((value) =>
                value === '' || value === null || value === undefined ? null : Number(value),
              )
              .integer('Trial Period Days must be an integer.')
              .min(0, 'Trial Period Days must be at least 0.')
              .max(365, 'Trial Period Days must be at most 365.');
          }

          return schema;
        }),
    });

    const handleNextStep = () => {
      const nextStep = CreateOrganizationStepConfig[createOrganizationStep].next;
      if (nextStep) {
        setCreateOrganizationStep(nextStep);
      }
    };

    const handleBackStep = () => {
      const backStep = CreateOrganizationStepConfig[createOrganizationStep].back;
      if (backStep) {
        setCreateOrganizationStep(backStep);
      }
    };

    const submitLabel = useMemo(() => {
      if (isEdit) {
        return 'Save Changes';
      }

      if (CreateOrganizationStepConfig[createOrganizationStep].next) {
        return 'Continue';
      }

      return 'Add Organization';
    }, [isEdit, createOrganizationStep]);

    const formik = useFormik({
      initialValues,
      validationSchema,
      onSubmit: async (values) => {
        let result;

        if (isEdit) {
          result = await handleEditOrganization(values);
        } else if (CreateOrganizationStepConfig[createOrganizationStep].next) {
          handleNextStep();
        } else {
          const addonsPayload = formik.values.addons
            ? Object.entries(formik.values.addons)
                .map(([key, value]) => (value === true ? key : null))
                .filter(Boolean)
            : [];

          const payload = {
            name: values.name,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            managerId: values.managerId,
            advisorId: values.advisorId,
            isTcrActive: values[PlatformFeatures.TCR],
            isPayingCustomer: values.isPayingCustomer,
            preferredLanguage: values.preferredLanguage,
            currentTierId: values.currentTierId,
            addons: addonsPayload,
            purchasedSeats: values.purchasedSeats,
          };

          if (values.isPayingCustomer) {
            Object.assign(payload, {
              billingInterval: values.billingInterval,
              billingModel: values.billingModel,
              startsAt: values.startsAt,
              endsAt: values.endsAt,
              // We only care about trial period days for credit card billing model
              trialPeriodDays:
                values.billingModel === BillingModel.CREDIT_CARD
                  ? values.trialPeriodDays
                  : undefined,
            });
          }

          result = (await createClientOrg(payload)) as ClientOrganization;
          // We need to create a Stripe customer for invoice billing model if they are paying customers
          if (
            result &&
            result?.id &&
            values.isPayingCustomer &&
            values.billingModel === BillingModel.INVOICE
          ) {
            await createStripeCustomer(result.id);
          }
        }

        if (result) {
          await refreshOrganizationViews(org?.id);
          await getOrgIdsByFeatureFlagConfig();
          onClose();
        }
      },
      enableReinitialize: true,
      validateOnChange: false,
    });

    useEffect(() => {
      if (open) {
        formik.resetForm();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const tierAddons = useMemo(
      () => getTierAddons(formik.values.currentTierId),
      [formik.values.currentTierId, getTierAddons],
    );

    const currentTierAddons = useCallback(
      () => {
        const checkAddonEnabled = (featureFlagKey: string) => {
          const addonEnabled = org?.organizationFeatures.find(
            (feature) => feature.feature.featureFlagKey === featureFlagKey,
          )?.enabledAt;
          return Boolean(addonEnabled);
        };

        const tierAddonsValues = tierAddons.reduce(
          (acc: Record<string, boolean>, addon) => ({
            ...acc,
            [addon.feature.featureFlagKey]: checkAddonEnabled(addon.feature.featureFlagKey),
          }),
          {},
        );
        formik.setFieldValue('addons', tierAddonsValues);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [org?.organizationFeatures, formik.values.currentTierId, tierAddons],
    );

    useEffect(() => {
      currentTierAddons();
    }, [currentTierAddons]);

    useEffect(() => {
      if (open) {
        setCreateOrganizationStep(CreateOrganizationSteps.ORGANIZATION_DETAILS);
      }
    }, [open]);

    useEffect(() => {
      if (!open || !isEdit || !org?.id) return;

      let isCurrent = true;
      setPreviewModePending(true);

      getOrganizationPreviewModeStatus({ organizationId: org.id }).then((status: unknown) => {
        if (isCurrent) {
          setPreviewModeEnabled(Boolean(status));
          setPreviewModePending(false);
        }
      });

      // eslint-disable-next-line consistent-return
      return () => {
        isCurrent = false;
      };
    }, [open, isEdit, org?.id, getOrganizationPreviewModeStatus]);

    // Kept separate from the Preview Mode effect so one failing Harness call
    // does not stop the other switch from loading.
    useEffect(() => {
      if (!open || !isEdit || !org?.id) return;

      let isCurrent = true;
      setDemoEmployeesPending(true);

      getOrganizationDemoEmployeesStatus({ organizationId: org.id }).then((status: unknown) => {
        if (isCurrent) {
          setDemoEmployeesEnabled(Boolean(status));
          setDemoEmployeesPending(false);
        }
      });

      // eslint-disable-next-line consistent-return
      return () => {
        isCurrent = false;
      };
    }, [open, isEdit, org?.id, getOrganizationDemoEmployeesStatus]);

    useEffect(() => {
      if (!open || !isEdit || !org?.id) return;

      let isCurrent = true;
      setAnalyticsEventsPending(true);

      getOrganizationAnalyticsEventsStatus({ organizationId: org.id }).then((status: unknown) => {
        if (isCurrent) {
          setAnalyticsEventsEnabled(typeof status === 'boolean' ? status : null);
          setAnalyticsEventsPending(false);
        }
      });

      // eslint-disable-next-line consistent-return
      return () => {
        isCurrent = false;
      };
    }, [open, isEdit, org?.id, getOrganizationAnalyticsEventsStatus, analyticsEventsReloadKey]);

    const handleTogglePreviewMode = async () => {
      if (!org?.id) return;

      const nextEnabled = !previewModeEnabled;
      setPreviewModeEnabled(nextEnabled);
      setPreviewModePending(true);

      const result = await changeOrganizationPreviewMode({
        organizationId: org.id,
        isEnabled: nextEnabled,
      });

      setPreviewModePending(false);

      if (!result) {
        setPreviewModeEnabled(!nextEnabled);
      } else {
        toast(`Preview Mode ${nextEnabled ? 'enabled' : 'disabled'}`, { type: 'success' });
      }
    };

    const handleToggleDemoEmployees = async () => {
      if (!org?.id) return;

      const nextEnabled = !demoEmployeesEnabled;
      setDemoEmployeesEnabled(nextEnabled);
      setDemoEmployeesPending(true);

      const result = await changeOrganizationDemoEmployees({
        organizationId: org.id,
        isEnabled: nextEnabled,
      });

      setDemoEmployeesPending(false);

      if (!result) {
        setDemoEmployeesEnabled(!nextEnabled);
      } else {
        toast(`Test employees ${nextEnabled ? 'enabled' : 'disabled'}`, { type: 'success' });
      }
    };

    const handleRetryAnalyticsEvents = () => setAnalyticsEventsReloadKey((key) => key + 1);

    const handleToggleAnalyticsEvents = async () => {
      // Never toggle from an unknown reading: it could write the opposite of the real state.
      if (!org?.id || analyticsEventsEnabled === null) return;

      const nextEnabled = !analyticsEventsEnabled;
      setAnalyticsEventsEnabled(nextEnabled);
      setAnalyticsEventsPending(true);

      const result = await changeOrganizationAnalyticsEvents({
        organizationId: org.id,
        isEnabled: nextEnabled,
      });

      setAnalyticsEventsPending(false);

      // The mutation returns the saved value, so `false` is a successful disable;
      // only a missing result (the store already toasted the error) means failure.
      if (typeof result !== 'boolean') {
        setAnalyticsEventsEnabled(!nextEnabled);
      } else {
        setAnalyticsEventsEnabled(result);
        toast(`Analytics events ${result ? 'enabled' : 'disabled'}`, { type: 'success' });
      }
    };

    return (
      <CustomDrawer
        open={open}
        onClose={onClose}
        headerLabel={isEdit ? 'Edit Organization' : 'Add Organization'}
        headerIcon={isEdit ? <BorderColorOutlined /> : <AddIcon />}
      >
        <form style={sx.formWrapper} onSubmit={formik.handleSubmit}>
          <div style={sx.formContainer}>
            {createOrganizationStep === CreateOrganizationSteps.ORGANIZATION_DETAILS && (
              <OrganizationDetailsStep
                isEdit={isEdit}
                formik={formik}
                advisors={advisors || []}
                managers={managers || []}
                currentUser={user}
                isAccountManager={isAccountManager}
                org={org}
                previewModeEnabled={previewModeEnabled}
                previewModePending={previewModePending}
                onTogglePreviewMode={handleTogglePreviewMode}
                demoEmployeesEnabled={demoEmployeesEnabled}
                demoEmployeesPending={demoEmployeesPending}
                onToggleDemoEmployees={handleToggleDemoEmployees}
                analyticsEventsEnabled={analyticsEventsEnabled}
                analyticsEventsPending={analyticsEventsPending}
                onToggleAnalyticsEvents={handleToggleAnalyticsEvents}
                onRetryAnalyticsEvents={handleRetryAnalyticsEvents}
              />
            )}
            {createOrganizationStep === CreateOrganizationSteps.ADMIN_DETAILS && (
              <OrganizationAdminStep formik={formik} />
            )}
            {createOrganizationStep === CreateOrganizationSteps.BILLING_DETAILS && (
              <OrganizationBillingStep formik={formik} tiers={tiers} tierAddons={tierAddons} />
            )}
          </div>
          <div style={sx.button}>
            {CreateOrganizationStepConfig[createOrganizationStep].back && (
              <CustomButton
                variant="transparent"
                type="button"
                onClick={handleBackStep}
                icon={<ArrowBackIos />}
              >
                Back
              </CustomButton>
            )}
            <CustomButton variant="default" type="submit" isLoading={formik.isSubmitting}>
              {submitLabel}
            </CustomButton>
          </div>
        </form>
        {/* Confirm Plan Change modal */}
        <ConfirmationModal
          isOpen={isPayCustomerChangedModalOpen}
          onClose={() => setIsPayCustomerChangedModalOpen(false)}
          variant="warning"
          title={`Are you sure you want to change ${org?.name} to a ${
            !formik.values.isPayingCustomer ? 'Demo Account' : 'Paid Account'
          }?`}
          confirmationText={org?.name || ''}
          icon={<SyncAlt sx={{ color: COLORS.NEUTRAL_DARK_2 }} />}
          confirmationButtonLabel="Confirm"
          onConfirm={async () => {
            const result = await handleEditOrganization(formik.values, false);
            if (result) {
              await refreshOrganizationViews(org?.id);
              setIsPayCustomerChangedModalOpen(false);
              onClose();
            }
          }}
        >
          {!formik.values.isPayingCustomer ? (
            <>
              <p>
                This will convert the organization to a demo account at ElektraFi. Demo accounts are
                not charged to your payment method and all features remain fully accessible
                according with the selected plan.
              </p>
              <p>To proceed, please type the organization name below:</p>
              <p>
                <strong>{org?.name}</strong>
              </p>
            </>
          ) : (
            <>
              <p>
                This will convert the organization from a <strong>Demo Account</strong> to a{' '}
                <strong>Paid Account</strong> at ElektraFi. This change will affect billing, payment
                method, and account management. Charges will begin based on the selected plan,
                number of seats, and payment method will be charged according to the billing cycle
                previously set.
              </p>
              <p>To proceed, please type the organization name below:</p>
              <p>
                <strong>{org?.name}</strong>
              </p>
            </>
          )}
        </ConfirmationModal>
      </CustomDrawer>
    );
  },
);
